import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { remove } from 'aws-amplify/storage';
import { useAuth } from '../../contexts/AuthContext';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { extractStorageKey } from '../../utils/storageImageUrl';
import StorageImage from '../StorageImage';

// Use Cognito user pool auth for admin operations (updates/deletes require authentication)
const client = generateClient({ authMode: 'userPool' });

interface Sponsor {
  id: string;
  name: string;
  description?: string;
  logoUrl?: string;
  websiteUrl?: string;
  contactEmail?: string;
  contactPhone?: string;
  displayOrder: number;
  isActive: boolean;
  sponsorshipLevel: 'GOLD' | 'SILVER' | 'BRONZE';
  sponsorshipAmount?: number;
  category?: 'TACKLE_SHOP' | 'BOAT_DEALER' | 'RESTAURANT' | 'ACCOMMODATION' | 'SERVICE' | 'OTHER';
  createdAt: string;
  updatedAt: string;
}

type SponsorFormData = Omit<Sponsor, 'id' | 'createdAt' | 'updatedAt'>;

const SponsorManagement: React.FC = () => {
  const { userId } = useAuth();
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState<Sponsor | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('ALL');
  const [uploading, setUploading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const [formData, setFormData] = useState<SponsorFormData>({
    name: '',
    description: '',
    logoUrl: '',
    websiteUrl: '',
    contactEmail: '',
    contactPhone: '',
    displayOrder: 0,
    isActive: true,
    sponsorshipLevel: 'BRONZE',
    sponsorshipAmount: undefined,
    category: 'OTHER',
  });

  useEffect(() => {
    loadSponsors();
  }, []);

  const loadSponsors = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await client.models.Sponsor.list();
      if (result.errors && result.errors.length > 0) {
        console.error('GraphQL errors loading sponsors:', result.errors);
        setError(`Failed to load sponsors: ${result.errors[0].message}`);
        return;
      }
      if (result.data) {
        const sorted = [...result.data].sort((a, b) => {
          const levelOrder = { GOLD: 1, SILVER: 2, BRONZE: 3 };
          const levelDiff = levelOrder[a.sponsorshipLevel as keyof typeof levelOrder] - 
                           levelOrder[b.sponsorshipLevel as keyof typeof levelOrder];
          if (levelDiff !== 0) return levelDiff;
          return a.displayOrder - b.displayOrder;
        });
        setSponsors(sorted as Sponsor[]);
      }
    } catch (err) {
      console.error('Error loading sponsors:', err);
      setError('Failed to load sponsors');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showToast('Please select an image file', 'error');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showToast('Logo size must be less than 5MB', 'error');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setFormData({ ...formData, logoUrl: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setUploading(true);
      let logoUrl = formData.logoUrl;

      if (logoFile) {
        // Server-side validated presigned URL flow (admin-only, MIME + size enforced in Lambda)
        const result = await (client.queries as any).generateUploadUrl({
          purpose: 'SPONSOR_LOGO',
          fileName: logoFile.name,
          contentType: logoFile.type,
          identityId: userId || '',
        });
        const uploadData = parseLambdaResponse(result?.data);
        if (!uploadData?.uploadUrl || !uploadData?.s3Key) throw new Error('Failed to get upload URL');

        await fetch(uploadData.uploadUrl, {
          method: 'PUT',
          body: logoFile,
          headers: { 'Content-Type': logoFile.type },
        });

        // Store the plain S3 key, not a baked/presigned URL — see
        // storageImageUrl.ts for why a stripped presigned URL never
        // actually works for this guest-readable-but-IAM-gated path.
        logoUrl = uploadData.s3Key;
      }

      if (editingSponsor) {
        if (logoFile && editingSponsor.logoUrl) {
          try {
            const oldKey = extractStorageKey(editingSponsor.logoUrl);
            if (oldKey) await remove({ path: oldKey });
          } catch (err) {
            console.error('Error deleting old logo:', err);
          }
        }
        await client.models.Sponsor.update({
          id: editingSponsor.id,
          ...formData,
          logoUrl,
        });
        showToast('Sponsor updated successfully', 'success');
      } else {
        const createResult = await client.models.Sponsor.create({
          ...formData,
          logoUrl,
        });
        if (createResult.errors && createResult.errors.length > 0) {
          console.error('GraphQL errors creating sponsor:', createResult.errors);
          showToast(`Failed to create: ${createResult.errors[0].message}`, 'error');
          return;
        }
        showToast('Sponsor created successfully', 'success');
      }
      resetForm();
      await loadSponsors();
    } catch (err) {
      console.error('Error saving sponsor:', err);
      showToast('Failed to save sponsor', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (sponsor: Sponsor) => {
    setEditingSponsor(sponsor);
    setFormData({
      name: sponsor.name,
      description: sponsor.description || '',
      logoUrl: sponsor.logoUrl || '',
      websiteUrl: sponsor.websiteUrl || '',
      contactEmail: sponsor.contactEmail || '',
      contactPhone: sponsor.contactPhone || '',
      displayOrder: sponsor.displayOrder,
      isActive: sponsor.isActive,
      sponsorshipLevel: sponsor.sponsorshipLevel,
      sponsorshipAmount: sponsor.sponsorshipAmount,
      category: sponsor.category || 'OTHER',
    });
    setLogoPreview('');
    setLogoFile(null);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this sponsor?')) return;
    try {
      const result = await client.models.Sponsor.delete({ id });
      if (result.errors && result.errors.length > 0) {
        showToast(`Failed to delete: ${result.errors[0].message}`, 'error');
        return;
      }
      showToast('Sponsor deleted', 'success');
      await loadSponsors();
    } catch (err) {
      console.error('Error deleting sponsor:', err);
      showToast('Failed to delete sponsor', 'error');
    }
  };

  const handleToggleActive = async (sponsor: Sponsor) => {
    try {
      await client.models.Sponsor.update({
        id: sponsor.id,
        isActive: !sponsor.isActive,
      });
      await loadSponsors();
    } catch (err) {
      console.error('Error toggling active status:', err);
      showToast('Failed to update status', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      logoUrl: '',
      websiteUrl: '',
      contactEmail: '',
      contactPhone: '',
      displayOrder: 0,
      isActive: true,
      sponsorshipLevel: 'BRONZE',
      sponsorshipAmount: undefined,
      category: 'OTHER',
    });
    setEditingSponsor(null);
    setShowForm(false);
    setLogoFile(null);
    setLogoPreview('');
  };

  const filteredSponsors = sponsors.filter(sponsor => {
    const matchesSearch = sponsor.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLevel = levelFilter === 'ALL' || sponsor.sponsorshipLevel === levelFilter;
    return matchesSearch && matchesLevel;
  });

  const getLevelBadge = (level: string) => {
    const styles = {
      GOLD: 'bg-yellow-100 text-yellow-800 border-yellow-400',
      SILVER: 'bg-gray-100 text-gray-800 border-gray-400',
      BRONZE: 'bg-amber-100 text-amber-800 border-amber-600',
    };
    return styles[level as keyof typeof styles] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {toast && (
        <div role="alert" aria-live="assertive" className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg shadow-lg text-white text-sm transition-all ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {toast.message}
        </div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Sponsor Management</h2>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700"
        >
          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Sponsor
        </button>
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search sponsors..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sponsorship Level</label>
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
            >
              <option value="ALL">All Levels</option>
              <option value="GOLD">Gold</option>
              <option value="SILVER">Silver</option>
              <option value="BRONZE">Bronze</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fishing-blue"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={loadSponsors} className="text-fishing-blue hover:text-blue-700">
              Try Again
            </button>
          </div>
        ) : filteredSponsors.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <p className="mt-2 text-sm text-gray-500">No sponsors found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {filteredSponsors.map((sponsor) => (
              <div key={sponsor.id} className={`border-2 rounded-lg p-6 hover:shadow-lg transition-shadow ${getLevelBadge(sponsor.sponsorshipLevel)}`}>
                <div className="flex justify-between items-start mb-4">
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getLevelBadge(sponsor.sponsorshipLevel)}`}>
                    {sponsor.sponsorshipLevel}
                  </span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    sponsor.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {sponsor.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {sponsor.logoUrl ? (
                  <div className="flex justify-center mb-4">
                    <StorageImage
                      storageKey={sponsor.logoUrl}
                      alt={sponsor.name}
                      className="max-h-24 max-w-full object-contain"
                      fallback={
                        <div className="flex justify-center items-center h-24 bg-gray-100 rounded mb-4 w-full">
                          <span className="text-gray-400 text-sm">No Logo</span>
                        </div>
                      }
                    />
                  </div>
                ) : (
                  <div className="flex justify-center items-center h-24 bg-gray-100 rounded mb-4">
                    <span className="text-gray-400 text-sm">No Logo</span>
                  </div>
                )}

                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">{sponsor.name}</h3>
                
                {sponsor.description && (
                  <p className="text-sm text-gray-600 text-center mb-3 line-clamp-2">
                    {sponsor.description}
                  </p>
                )}

                <div className="space-y-1 text-xs text-gray-500 mb-4">
                  {sponsor.websiteUrl && (
                    <div className="flex items-center justify-center">
                      <a href={sponsor.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-fishing-blue hover:underline">
                        Visit Website →
                      </a>
                    </div>
                  )}
                  {sponsor.contactEmail && (
                    <div className="text-center">{sponsor.contactEmail}</div>
                  )}
                  <div className="text-center">Display Order: {sponsor.displayOrder}</div>
                </div>

                <div className="flex justify-center gap-2 pt-4 border-t">
                  <button
                    onClick={() => handleToggleActive(sponsor)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                    title={sponsor.isActive ? 'Deactivate' : 'Activate'}
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                  <button onClick={() => handleEdit(sponsor)} className="p-2 text-fishing-blue hover:text-blue-700">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button onClick={() => handleDelete(sponsor.id)} className="p-2 text-red-600 hover:text-red-700">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-lg font-medium text-gray-900">
                {editingSponsor ? 'Edit Sponsor' : 'New Sponsor'}
              </h3>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sponsor Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logo (Optional)
                </label>
                <div className="mt-1 flex items-center gap-4">
                  <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                    <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Choose Logo
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                  </label>
                  {(logoPreview || editingSponsor?.logoUrl) && (
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Upload a logo (max 5MB). Supported formats: JPG, PNG, GIF
                </p>
                {logoPreview ? (
                  <div className="mt-3">
                    <img 
                      src={logoPreview} 
                      alt="Preview" 
                      className="max-h-32 rounded border border-gray-300"
                    />
                  </div>
                ) : editingSponsor?.logoUrl ? (
                  <div className="mt-3">
                    <StorageImage
                      storageKey={editingSponsor.logoUrl}
                      alt="Current sponsor logo"
                      className="max-h-32 rounded border border-gray-300"
                    />
                  </div>
                ) : null}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sponsorship Level *
                  </label>
                  <select
                    required
                    value={formData.sponsorshipLevel}
                    onChange={(e) => setFormData({ ...formData, sponsorshipLevel: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  >
                    <option value="BRONZE">Bronze</option>
                    <option value="SILVER">Silver</option>
                    <option value="GOLD">Gold</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  >
                    <option value="OTHER">Other</option>
                    <option value="TACKLE_SHOP">Tackle Shop</option>
                    <option value="BOAT_DEALER">Boat Dealer</option>
                    <option value="RESTAURANT">Restaurant</option>
                    <option value="ACCOMMODATION">Accommodation</option>
                    <option value="SERVICE">Service</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website URL
                  </label>
                  <input
                    type="url"
                    value={formData.websiteUrl}
                    onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                    placeholder="https://example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sponsorship Amount (AUD)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.sponsorshipAmount || ''}
                  onChange={(e) => setFormData({ ...formData, sponsorshipAmount: parseFloat(e.target.value) || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 text-fishing-blue focus:ring-fishing-blue border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">Active</label>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={uploading}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                >
                  {uploading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    `${editingSponsor ? 'Update' : 'Create'} Sponsor`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SponsorManagement;
